import { useState, useEffect, useRef, useContext } from 'react';
import { VideoCarouselContext } from '../VideoCarousel/VideoCarouselContext';
import { useVideoRef } from './useVideoRef';
export const useVideoPlayer = ({ autoplay, playOnHover, disabled, disabledUntilHover, toggleVolumeOnClick, playButtonOnLoad, includeSound, videoIndex, hasBlurbOrMetadata, preload, }) => {
    const { shouldCarouselPlay, setShouldCarouselPlay, isVideoPlaying, setIsVideoPlaying, } = useContext(VideoCarouselContext);
    const shouldAutoPlay = autoplay && !disabled;
    const { ref, play, pause, isPlaying, isMuted, mute, unMute, startTime, isLoadState, isLoading, } = useVideoRef({
        muteOnLoad: shouldAutoPlay || playOnHover || !includeSound,
        autoplay: shouldAutoPlay || false,
        videoIndex,
        preload,
    });
    const hoverable = typeof window !== 'undefined'
        ? window.matchMedia('(hover: hover').matches
        : false;
    const [isFocus, setIsFocus] = useState(false);
    const [showFullControls, setShowFullControls] = useState(false);
    const [shouldPlayOnHover, setShouldPlayOnHover] = useState(hoverable &&
        playOnHover &&
        (shouldCarouselPlay === undefined || shouldCarouselPlay));
    useEffect(() => {
        if (playOnHover && shouldCarouselPlay && !shouldPlayOnHover) {
            setShouldPlayOnHover(true);
        }
        if (playOnHover && shouldCarouselPlay === false && shouldPlayOnHover) {
            setShouldPlayOnHover(false);
        }
    }, [playOnHover, shouldCarouselPlay, shouldPlayOnHover]);
    const showPlayButtonOnly = isLoadState && playButtonOnLoad;
    const [showDisabledOverlay, setShowDisabledOverlay] = useState(disabled || disabledUntilHover);
    const togglePlayButton = () => {
        if (!isPlaying) {
            play();
            setShouldCarouselPlay === null || setShouldCarouselPlay === void 0 ? void 0 : setShouldCarouselPlay(true);
            if (playOnHover && !shouldPlayOnHover) {
                setShouldPlayOnHover(true);
            }
        }
        else {
            pause();
            setShouldCarouselPlay === null || setShouldCarouselPlay === void 0 ? void 0 : setShouldCarouselPlay(false);
            if (playOnHover) {
                setShouldPlayOnHover(false);
            }
        }
    };
    const disabledRef = useRef(disabled);
    const disabledUntilHoverRef = useRef(disabledUntilHover);
    useEffect(() => {
        if (disabledRef.current !== disabled ||
            disabledUntilHoverRef.current !== disabledUntilHover) {
            disabledRef.current = disabled;
            disabledUntilHoverRef.current = disabledUntilHover;
            if (disabled || disabledUntilHover) {
                setShowDisabledOverlay(true);
                setShowFullControls(false);
            }
            else {
                if (showDisabledOverlay) {
                    setShowDisabledOverlay(false);
                }
                if (isFocus) {
                    setShowFullControls(true);
                }
            }
        }
    }, [
        autoplay,
        disabled,
        disabledUntilHover,
        isFocus,
        isPlaying,
        pause,
        play,
        playOnHover,
        shouldAutoPlay,
        shouldPlayOnHover,
        showDisabledOverlay,
        videoIndex,
        hasBlurbOrMetadata,
        isVideoPlaying,
        setIsVideoPlaying,
    ]);
    const onMouseEnter = () => {
        if (hoverable) {
            if (shouldPlayOnHover && !disabled && !isPlaying) {
                play();
            }
            if (!disabled) {
                setShowFullControls(true);
            }
            setShowDisabledOverlay(false);
        }
    };
    const onMouseLeave = () => {
        if (hoverable) {
            if (!disabled) {
                setShowFullControls(false);
            }
            if (shouldPlayOnHover && isPlaying) {
                pause();
            }
            setShowDisabledOverlay(Boolean(disabled || disabledUntilHover));
        }
    };
    const onFocus = () => {
        setIsFocus(true);
        if (hoverable && shouldPlayOnHover && !disabled && !isPlaying) {
            play();
        }
        if (!disabled) {
            setShowFullControls(true);
        }
        setShowDisabledOverlay(false);
    };
    const onBlur = () => {
        if (hoverable && shouldPlayOnHover && isPlaying) {
            pause();
        }
        setShowFullControls(false);
        setShowDisabledOverlay(Boolean(disabled || disabledUntilHover));
    };
    const toggleVolume = () => {
        if (isMuted) {
            unMute();
        }
        else {
            mute();
        }
    };
    const onClick = () => {
        if (!disabled) {
            if (hoverable) {
                if (toggleVolumeOnClick) {
                    toggleVolume();
                }
                else {
                    togglePlayButton();
                }
            }
            else {
                if (isLoadState && showPlayButtonOnly) {
                    togglePlayButton();
                }
                else {
                    setShowFullControls(!showFullControls);
                }
            }
        }
    };
    const onKeyboardEnter = () => {
        if (!disabled) {
            if (hoverable) {
                if (toggleVolumeOnClick) {
                    toggleVolume();
                }
                else {
                    togglePlayButton();
                }
            }
        }
    };
    return {
        ref,
        showFullControls,
        showPlayButtonOnly,
        showDisabledOverlay,
        onMouseEnter,
        onMouseLeave,
        onFocus,
        onBlur,
        onClick,
        onKeyboardEnter,
        togglePlayButton,
        toggleVolume,
        startTime,
        isPlaying,
        isMuted,
        isLoading,
    };
};
//# sourceMappingURL=useVideoPlayer.js.map